<template>
  <template v-if="!productDescription && !sellingPoints.length">
    <div class="py-4">
      {{ $t('pdp.product_details_no_info') }}
    </div>
  </template>
  <div
    v-else-if="productDescription"
    class="text-md py-4 font-normal leading-5 md:text-lg md:leading-6"
  >
    <!--
      Plain (desktop) variant matches the Figma: description first, selling
      points below, and no order number. The bars (mobile) variant keeps its
      original order (order number, selling points, then description).
    -->
    <template v-if="isPlain">
      <div>{{ productDescription }}</div>
      <ProductBenefitsNew v-if="product" :product="product" />
    </template>
    <template v-else>
      <span class="text-sm text-gray-secondary md:block">
        {{ $t('pdp.main_order_number') }}
        <span data-test-id="product-order-number">{{
          productOrderNumber
        }}</span>
      </span>
      <ProductBenefitsNew v-if="product" :product="product" />
      <div>
        {{ productDescription }}
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import type { PropType, Ref } from 'vue'
import type { Product } from '@scayle/storefront-nuxt'
import {
  getAdvancedAttributeFirstValue,
  getProductDescription,
} from '#layers/base/utils/advancedAttribute'

type AttributeValue = string | number | null | undefined

const props = defineProps({
  product: {
    type: Object as PropType<Product | null>,
    default: () => null,
  },
  productOrderNumber: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
})

// Provided by ProductDetailsAccordion: 'plain' is the desktop in-context
// layout, 'bars' is the mobile accordion.
const variant = inject<Ref<'bars' | 'plain'>>(
  'productDetailsVariant',
  ref('bars'),
)
const isPlain = computed(() => variant.value === 'plain')

const productDescription = computed<AttributeValue>(() => {
  return getProductDescription(props.product)
})

const sellingPoints = computed(() => {
  const keys = [
    'sellingPoint1Dessin',
    'sellingPoint2Material',
    'sellingPoint3AGSSpezialDetail',
    'sellingPoint4InternEinkauf',
  ]
  const values = keys.map((key) =>
    getAdvancedAttributeFirstValue(props.product, key),
  )

  return values.filter((value): value is string => Boolean(value))
})
</script>
